import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { Dialog, DialogHeader, ErrorLine, PillField, Spinner } from '../customer/CustomersUI';
import { COPY } from './stock.copy';

/**
 * Cancelling a movement — which is the only way to undo one.
 *
 * There is no DELETE route: the document is kept, reversed, and stamped with
 * who cancelled it and why. Stock is money, so a correction has to remain
 * visible.
 *
 * The two sentences are the phone's, and they are the ones that matter,
 * because what cancelling does depends on which way the document pointed. One
 * refusal is worth knowing about in advance and is NOT checked here: the
 * server will not un-receive a delivery whose stock has already been issued.
 * It says so clearly, and that message is shown as it comes back.
 */
export default function CancelMovementDialog({ movement, currency, onClose, onConfirm }) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const isIn = movement.type === 'in';

  const confirm = async () => {
    if (busy) return;

    setBusy(true);
    setError('');

    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (requestError) {
      setError(requestError.message || 'Could not cancel');
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={460}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.cancelTitle(movement.no)} onClose={onClose} locked={busy} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            margin: '16px 0 12px',
            padding: '12px 14px',
            background: T.sunk,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusControl,
          }}
        >
          <span style={{ fontSize: 12.5, color: T.inkSoft }}>
            {movement.reason} · {(movement.lines || []).length} line
            {(movement.lines || []).length === 1 ? '' : 's'}
          </span>
          <b style={{ fontSize: 14, ...NUMERIC }}>{money(movement.total, currency)}</b>
        </div>

        <p style={{ margin: 0, fontSize: 13.5, color: T.ink, lineHeight: 1.5 }}>
          {isIn ? COPY.cancelInBody : COPY.cancelOutBody}
        </p>

        <div style={{ paddingTop: 14 }}>
          <PillField
            name="cancel-reason"
            value={reason}
            onChange={setReason}
            onEnter={confirm}
            placeholder={COPY.cancelHint}
            disabled={busy}
            autoFocus
          />
        </div>

        <div style={{ paddingTop: 14 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              height: 44,
              padding: '0 18px',
              border: `1px solid ${T.borderStrong}`,
              borderRadius: T.radiusControl,
              background: T.surface,
              font: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              color: T.inkSoft,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {COPY.keepIt}
          </button>

          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 44,
              padding: '0 22px',
              border: 'none',
              borderRadius: T.radiusControl,
              background: T.danger,
              font: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              color: T.surface,
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? <Spinner /> : null}
            {COPY.cancelIt}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
